import { ExClinVarResponse } from "@/types/clinvar.model";
import { QueryVariant, QueryVariant_Genotype } from "@gen/lda/v1alpha1/genomicquery_pb";

export type ClinicalSignificance = 'Pathogenic' | 'Likely Pathogenic' | 'VUS' | 'Likely Benign' | 'Benign';

export type Zygosity = 
  | 'GENOTYPE_UNKNOWN'
  | 'HOMOZYGOUS_REF'
  | 'HETEROZYGOUS'
  | 'HOMOZYGOUS_ALT'
  | 'HETEROZYGOUS_ALT';

export interface PatientInfo {
  id: string;
  name?: string;
  dateOfBirth?: string;
  sex?: string;
  sampleId?: string;
  collectionDate?: string;
}

export interface ConditionDetails {
  name: string;
  description: string;
  inheritance_pattern?: string;
  mondo_id?: string;
  clinical_management_considerations?: string;
  omim_id?: string;
  orphanet_id?: string;
  medgen_id?: string;
  limitations?: string[];
}

export interface AnnotationFeature {
  dataset_id: string;
  chromosome: string;
  start: string;
  end: string;
  strand: string;
  feature_type: string;
  source: string;
  id: string;
  name: string;
  gene_id: string;
  gene_name: string;
  transcript_id: string;
  exon_number: number;
  biotype: string;
  reference: string;
  alternate: string;
  clinical_significance: string;
  attributes: Record<string, string>;
}

export interface VariantModifier {
  mondo?: {
    mondo_id: string;
    pathogenicity: string;
  };
  annotation?: {
    features: AnnotationFeature[];
  };
  modifier: string;
}

export interface RawVariant {
  alternate: string[];
  variant_modifier: VariantModifier[];
  chromosome: string;
  position: string;
  reference: string;
  genotype: string;
  quality: number;
}

/*export interface GenomicVariant {
  chromosome: string;
  position: bigint;
  referenceAllele: string;
  alternateAllele: string;
  gene: string;
  transcript: string;
  hgvsc: string;
  hgvsp: string;
  clinicalSignificance: ClinicalSignificance;
  zygosity: QueryVariant_Genotype;
  inheritance?: string;
  condition?: string;
  evidence?: string[];
  references?: string[];
  drug?: string;
  effect?: string;
  dosage?: string;
  duration?: string;
  frequency?: string;
  route?: string;
  sideEffects?: string[];
  contraindications?: string[];
  monitoring?: string[];
  alternative?: string[];
  notes?: string[];
  clinicalImplications?: string;
  clinicalRecommendations?: string;
  clinicalGuidelines?: string;
  clinicalEvidence?: string;
  clinicalReferences?: string;
}*/

export interface DrugResponseVariant extends QueryVariantWithClinvar {
  drug: string;
  effect: string;
  recommendedAction?: string;
}

export interface ReportSection {
  title: string;
  variants: QueryVariantWithClinvar[];
  description?: string;
}

export interface RawReport {
  clinical_summary: {
    conditions: ConditionDetails[];
  };
}

export interface RawVariants {
  variants: RawVariant[];
}


export interface GenomicReportMetrics {
  variantsReturned: number;
  variantsMatchedInVcf: number;
  totalTimeMs: number;
  filterTimeMs: number;
  vcfReadTimeMs: number;
  variantsPerSecond: number;
  vcfBytesRead: number;
  vcfBlocksProcessed: number;
}


export interface GenomicReport {
  name: string;
  description: string;
  conditions: ConditionDetails[];
  pathogenicVariants: QueryVariantWithClinvar[];
  drugResponseVariants?: DrugResponseVariant[];
  vusVariants: QueryVariantWithClinvar[];
  references: string[];
  disclaimer: string;
  metrics: GenomicReportMetrics;
  processingDetails: {
    pipeline: string;
    version: string;
    date: string;
    analyst?: string;
  };
} 


export interface QueryVariantWithClinvar extends QueryVariant {
  clinvar: ExClinVarResponse
}

export interface GeneStructure {
  name: string;
  chromosome: string;
  start: number;
  end: number;
  strand: '+' | '-';
  exons: Array<{
    start: number;
    end: number;
  }>;
}

  
export const getGenotypeString = (genotype: QueryVariant_Genotype) => {
  switch (genotype) {
    case QueryVariant_Genotype.HETEROZYGOUS_ALT:
      return 'Heterozygous';
    case QueryVariant_Genotype.HOMOZYGOUS_ALT:
      return 'Homozygous';
    case QueryVariant_Genotype.HOMOZYGOUS_REF:
      return 'Homozygous';
    case QueryVariant_Genotype.GENOTYPE_UNKNOWN:
      return 'Unknown';
    case QueryVariant_Genotype.HETEROZYGOUS:
      return 'Heterozygous';
    default:
      return 'Unknown';
  }
}

export const getClinicalSignificanceString = (clinicalSignificance: string) => {
  switch (clinicalSignificance) {
    case 'Pathogenic':
      return 'Pathogenic';
    case 'Likely Pathogenic':
      return 'Likely Pathogenic';
    case 'Conflicting_classifications_of_pathogenicity':
      return 'Conflicting';
    case 'Benign':
      return 'Benign';
    case 'Likely Benign':
      return 'Likely Benign';
    case 'VUS':
      return 'VUS';
  }

  return clinicalSignificance.replace('_', ' ');
}


export interface PatientDetails {
  patientName: string;
  gender: string;
  dateOfBirth: string;
  collectionTime: string;
}
